/* eslint-disable @typescript-eslint/naming-convention */
import { useCheckoutPricing } from "@recurly/react-recurly";
import { debounce } from "lodash";
import { useCallback, useEffect } from "react";
import { useSelector } from "react-redux";

import { getBilling } from "store/entities/billing";
import { selectCurrentSubscription } from "store/entities/subscriptions";

import { preparePlanCode } from "./helpers";

const CHECKOUT_PRICING_DEBOUNCE_TIME = 500;

export interface ITaxChangePreview {
  isLoading: boolean;
  currentTax: number;
  newTax: number;
}

interface ITaxChangePreviewParams {
  country?: string;
  couponValue?: number;
  zipCode?: string;
}

interface IUseTaxChangePreviewResult {
  taxChangePreview: ITaxChangePreview;
  handleAddressChange(params: ITaxChangePreviewParams): void;
}

/**
 * Returns final prices of subscription and applications
 * - in case of fresh accounts without subscription or api error - returns basic pricing basing on frontend calculations
 * - for active subscription it returns extended pricing basing on subscription preview response
 */
export function useTaxChangePreview(): IUseTaxChangePreviewResult {
  const subscription = useSelector(selectCurrentSubscription);
  // @ts-expect-error TS(2339): Property 'billingInfo' does not exist on type 'unk... Remove this comment to see the full error message
  const { billingInfo } = useSelector(getBilling);

  const initialPricingInput = {
    subscriptions: [
      {
        plan: preparePlanCode(subscription.plan, subscription.billingCycle),
        quantity: subscription.agents,
      },
    ],
    address: {
      first_name: billingInfo?.firstName,
      last_name: billingInfo?.lastName,
      country: billingInfo?.country,
      postal_code: billingInfo?.zip,
    },
    coupon: subscription.coupon?.name,
  };

  const [{ price: initialPrice, loading: loadingInitialPrice }, setNewInitialPrice] =
    useCheckoutPricing(initialPricingInput);
  const [{ price: newPrice, loading: loadingNewPrice }, setCheckoutPricing] = useCheckoutPricing(initialPricingInput);

  useEffect(() => setNewInitialPrice(initialPricingInput), [billingInfo?.country, billingInfo?.zip]);

  const debouncedHandleAddressChange = debounce(({ country, zipCode }: ITaxChangePreviewParams): void => {
    setCheckoutPricing({
      ...initialPricingInput,
      address: {
        ...initialPricingInput.address,
        country,
        postal_code: zipCode,
      },
    });
  }, CHECKOUT_PRICING_DEBOUNCE_TIME);

  const handleAddressChange = useCallback(debouncedHandleAddressChange, []);

  return {
    handleAddressChange,
    taxChangePreview: {
      isLoading: loadingInitialPrice || loadingNewPrice,
      currentTax: parseFloat(initialPrice.next?.taxes),
      newTax: parseFloat(newPrice.next?.taxes),
    },
  };
}
